/**
 * 贪吃蛇游戏 WebMCP API
 * 暴露游戏控制接口给 AI Agent
 */

// 多语言：i18n.js 提供 window.I18N.t(key)，双语字典在 docs/index.html 的 I18N_DICT 中
const I18N = (window as any).I18N;
function t(key: string, params?: Record<string, string | number>): string {
  let s = I18N && typeof I18N.t === 'function' ? I18N.t(key) : key;
  if (params) {
    Object.keys(params).forEach(k => { s = s.replace('{' + k + '}', String(params[k])); });
  }
  return s;
}

// 游戏状态类型
interface SnakeState {
  snake: { coordinates: Array<{x: number, y: number}> };
  snack: { coordinate: {x: number, y: number} };
  score: number;
  isPlaying: boolean;
  isGameOver: boolean;
  direction: string;
  gridSize: number;
}

// 回调类型
type StartGameCallback = (withBorders: boolean) => void;
type StopGameCallback = () => void;
type ChangeDirectionCallback = (direction: string) => void;

// 存储回调
let startGameCallback: StartGameCallback | null = null;
let stopGameCallback: StopGameCallback | null = null;
let changeDirectionCallback: ChangeDirectionCallback | null = null;
let getStateCallback: (() => SnakeState) | null = null;

/**
 * 设置游戏控制回调（由Vue组件调用）
 */
export function setGameCallbacks(
  start: StartGameCallback,
  stop: StopGameCallback,
  changeDir: ChangeDirectionCallback,
  getState: () => SnakeState
) {
  startGameCallback = start;
  stopGameCallback = stop;
  changeDirectionCallback = changeDir;
  getStateCallback = getState;
}

/**
 * 获取游戏状态
 */
export function getState(): SnakeState {
  if (getStateCallback) {
    return getStateCallback();
  }
  return {
    snake: { coordinates: [] },
    snack: { coordinate: {x: 0, y: 0} },
    score: 0,
    isPlaying: false,
    isGameOver: false,
    direction: 'right',
    gridSize: 35
  };
}

/**
 * 开始游戏
 */
export function startGame(withBorders: boolean = false): { success: boolean; message: string } {
  if (startGameCallback) {
    startGameCallback(withBorders);
    return { success: true, message: t('snake_started', { m: t(withBorders ? 'snake_bordered' : 'snake_unbordered') }) };
  }
  return { success: false, message: t('snake_not_init') };
}

/**
 * 停止游戏
 */
export function stopGame(): { success: boolean; message: string } {
  if (stopGameCallback) {
    stopGameCallback();
    return { success: true, message: t('snake_stopped') };
  }
  return { success: false, message: t('snake_not_init') };
}

/**
 * 改变方向
 */
export function changeDirection(direction: string): { success: boolean; message: string } {
  const validDirections = ['up', 'down', 'left', 'right'];
  if (!validDirections.includes(direction.toLowerCase())) {
    return { success: false, message: t('snake_invalid_dir', { d: direction }) };
  }

  if (changeDirectionCallback) {
    changeDirectionCallback(direction.toUpperCase());
    return { success: true, message: t('snake_dir_changed', { d: direction }) };
  }
  return { success: false, message: t('snake_not_init') };
}

/**
 * 初始化 WebMCP 接口
 */
export function initWebMCP() {
  if (typeof window === 'undefined') return;

  // 检查是否已经初始化
  if ((window as any).webmcpReady) {
    console.log('[Snake] WebMCP 已初始化，跳过重复注册');
    return;
  }

  // 暴露到全局
  (window as any).snakeGameAPI = {
    getState,
    startGame,
    stopGame,
    changeDirection
  };

  // 检查 navigator.modelContext 是否存在
  if (!(navigator as any).modelContext) {
    console.log('[Snake] navigator.modelContext 不可用，WebMCP 未注册');
    return;
  }

  const mc = (navigator as any).modelContext;

  // 注册工具（语言切换时按新语言重新注册）
  function registerTools(target: any) {
    // 注册工具：获取状态
    target.registerTool({
      name: 'snake_get_state',
      description: t('snake_get_state_desc'),
      inputSchema: {
        type: 'object',
        properties: {
          format: {
            type: 'string',
            enum: ['json', 'text'],
            description: t('snake_format_desc')
          }
        }
      },
      execute: (args: { format?: string }) => {
        const state = getState();
        if (args.format === 'text') {
          const snakeHead = state.snake.coordinates[0];
          let text = `${t('snake_score')}: ${state.score}\n`;
          text += `${t('snake_status')}: ${state.isPlaying ? t('snake_playing') : t('snake_stopped_status')}\n`;
          text += `${t('snake_head')}: (${snakeHead?.x}, ${snakeHead?.y})\n`;
          text += `${t('snake_len')}: ${state.snake.coordinates.length}\n`;
          text += `${t('snake_dir')}: ${state.direction}\n`;
          text += `${t('snake_food')}: (${state.snack.coordinate.x}, ${state.snack.coordinate.y})\n`;
          text += `${t('snake_grid')}: ${state.gridSize}x${state.gridSize}`;
          return { content: [{ type: 'text', text }] };
        }
        return { content: [{ type: 'text', text: JSON.stringify(state, null, 2) }] };
      }
    });

    // 注册工具：开始游戏
    target.registerTool({
      name: 'snake_start',
      description: t('snake_start_desc'),
      inputSchema: {
        type: 'object',
        properties: {
          withBorders: {
            type: 'boolean',
            description: t('snake_border_desc'),
            default: false
          }
        }
      },
      execute: (args: { withBorders?: boolean }) => {
        const result = startGame(args.withBorders || false);
        return { content: [{ type: 'text', text: JSON.stringify(result) }] };
      }
    });

    // 注册工具：停止游戏
    target.registerTool({
      name: 'snake_stop',
      description: t('snake_stop_desc'),
      inputSchema: {
        type: 'object',
        properties: {}
      },
      execute: () => {
        const result = stopGame();
        return { content: [{ type: 'text', text: JSON.stringify(result) }] };
      }
    });

    // 注册工具：改变方向
    target.registerTool({
      name: 'snake_change_direction',
      description: t('snake_dir_desc_full'),
      inputSchema: {
        type: 'object',
        properties: {
          direction: {
            type: 'string',
            enum: ['up', 'down', 'left', 'right'],
            description: t('snake_dir_desc')
          }
        },
        required: ['direction']
      },
      execute: (args: { direction: string }) => {
        const result = changeDirection(args.direction);
        return { content: [{ type: 'text', text: JSON.stringify(result) }] };
      }
    });
  }

  registerTools(mc);

  // 语言切换（i18n.js setLang() 会 dispatch games-i18n-change）→ 按新语言重新注册工具
  const TOOL_NAMES = ['snake_get_state', 'snake_start', 'snake_stop', 'snake_change_direction'];
  window.addEventListener('games-i18n-change', () => {
    try {
      TOOL_NAMES.forEach((n) => { try { mc.unregisterTool(n); } catch (e) {} });
      registerTools(mc);
    } catch (e) { console.warn('[Snake] 语言切换重注册失败', e); }
  });

  console.log('[Snake] WebMCP 工具已注册');
  (window as any).webmcpReady = true;

  // webmcp-a11y-expose.js 会自动暴露工具列表到无障碍树
}